import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register ONLY core plugins globally (NOT datalabels)
ChartJS.register(ArcElement, Tooltip, Legend);

// Styled Components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 100%;              /* fill parent card */
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
`;

const ChartArea = styled.div`
  position: relative;
  width: 100%;
  flex: 1 1 auto;            /* grow to fill remaining space */
  min-height: 0;             /* important in flex containers */
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const monthOptions = [
  { value: "ALL", label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

const Top10SalesShare = ({ data }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("ALL");
  const [chartData, setChartData] = useState(null);

  const calculateTransactionValue = (row) =>
    parseFloat(
      row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
    ) || 0;

  useEffect(() => {
    if (!data || data.length === 0) return;
    const years = Array.from(new Set(data.map((d) => parseInt(d.Rok, 10)))).sort((a, b) => b - a);
    setAvailableYears(years);
    if (!selectedYear) setSelectedYear(years[0]);
  }, [data]);

  useEffect(() => {
    if (!data || !selectedYear) return;

    const filtered = data.filter((d) => {
      const year = parseInt(d.Rok, 10);
      const month = parseInt(d.Miesiac, 10);
      return year === selectedYear && (selectedMonth === "ALL" || month === selectedMonth);
    });

    const totals = {};
    filtered.forEach((row) => {
      const name = row.NazwaKontrahenta || "Nieznany";
      const value = calculateTransactionValue(row);
      if (!totals[name]) totals[name] = 0;
      totals[name] += value;
    });

    const sorted = Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    if (sorted.length === 0) {
      setChartData(null);
      return;
    }

    const labels = sorted.map(([name]) =>
      name.length > 15 ? name.slice(0, 15) + "…" : name
    );
    const values = sorted.map(([, val]) => val);

    const bgColors = [
      "#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF",
      "#FF9F40", "#C9CBCF", "#8B0000", "#228B22", "#000080"
    ];

    setChartData({
      labels,
      datasets: [
        {
          label: "Udział w sprzedaży",
          data: values,
          backgroundColor: bgColors,
          borderColor: "#fff",
          borderWidth: 2,
        },
      ],
    });
  }, [data, selectedYear, selectedMonth]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Sprzedaż top 10 kontrahentów
        <InfoIconWrapper data-tooltip-id="sales-share-info">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="sales-share-info"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Sprzedaż top 10 kontrahentów</strong><br />
            Pokazuje 10 kontrahentów o największym udziale<br />
            w wartości sprzedaży dla wybranego okresu.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok: </label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </div>

        <div>
          <label>Miesiąc: </label>
          <Select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(e.target.value === "ALL" ? "ALL" : parseInt(e.target.value, 10))
            }
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <ChartArea>
          {/* datalabels enabled only for this chart */}
          <Pie
            data={chartData}
            plugins={[ChartDataLabels]}
            options={{
              responsive: true,
              maintainAspectRatio: false,   // fill container height
              plugins: {
                legend: { position: "bottom" },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const label = context.label || "";
                      const value = context.raw;
                      const total = context.dataset.data.reduce((sum, v) => sum + v, 0);
                      const percentage = total ? ((value / total) * 100).toFixed(1) : "0.0";
                      return `${label}: ${value.toLocaleString("pl-PL")} (${percentage}%)`;
                    },
                  },
                },
                datalabels: {
                  color: "#fff",
                  font: { size: 11, weight: "bold" },
                  formatter: (value, ctx) => {
                    const arr = ctx?.dataset?.data || [];
                    const total = arr.reduce((a, b) => a + b, 0);
                    return total ? `${((value / total) * 100).toFixed(1)}%` : "";
                  },
                },
              },
            }}
          />
        </ChartArea>
      ) : (
        <p style={{ textAlign: "center" }}>Brak danych dla wybranych filtrów.</p>
      )}
    </ChartWrapper>
  );
};

export default Top10SalesShare;
